import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { OwnerLayout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  PawPrint, Plus, Trash2, Edit2, Check, X, Play, Image, 
  Video, Sparkles, ChevronRight, BookOpen, Camera, Instagram, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OwnerDogs() {
  const navigate = useNavigate();
  const { user, activeDog, selectDog } = useAuth();
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddingDog, setIsAddingDog] = useState(false);
  const [editingDog, setEditingDog] = useState(null);
  const [viewingDog, setViewingDog] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    breed: '',
    age: '',
    photo_url: '',
    fun_facts: '',
    photos: [],
    videos: [],
    tiktok_url: '',
    instagram_url: ''
  });
  const [newPhotoUrl, setNewPhotoUrl] = useState('');
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchDogs();
  }, [user]);

  const fetchDogs = async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${API}/dogs/owner/${user.id}`);
      setDogs(response.data);
      
      // If no active dog and we have dogs, select the first one
      if (response.data.length > 0 && !activeDog) {
        selectDog(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching dogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || uploading) return;
    setSubmitting(true);
    
    try {
      const payload = {
        name: formData.name,
        breed: formData.breed,
        age: parseInt(formData.age),
        photo_url: formData.photo_url || null,
        fun_facts: formData.fun_facts || null,
        photos: formData.photos,
        videos: formData.videos,
        tiktok_url: formData.tiktok_url || null,
        instagram_url: formData.instagram_url || null
      };

      if (editingDog) {
        await axios.put(`${API}/dogs/${editingDog.id}`, payload);
        toast.success(`${formData.name}'s profile updated!`);
      } else {
        const response = await axios.post(`${API}/dogs`, {
          owner_user_id: user.id,
          ...payload
        });
        selectDog(response.data);
        toast.success(`Welcome to the family, ${formData.name}!`);
      }
      
      setIsAddingDog(false);
      setEditingDog(null);
      resetForm();
      fetchDogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save dog');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ 
      name: '', 
      breed: '', 
      age: '', 
      photo_url: '',
      fun_facts: '',
      photos: [],
      videos: [],
      tiktok_url: '',
      instagram_url: ''
    });
    setNewPhotoUrl('');
    setNewVideoUrl('');
    setSubmitting(false);
    setUploading(false);
  };

  const handleDelete = async (dog) => {
    if (!window.confirm(`Are you sure you want to remove ${dog.name}? This will delete all training progress.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/dogs/${dog.id}`);
      toast.success(`${dog.name} has been removed`);
      
      if (activeDog?.id === dog.id) {
        selectDog(null);
      }
      
      fetchDogs();
    } catch (error) {
      toast.error('Failed to delete dog');
    }
  };

  const openEditDialog = (dog) => {
    setEditingDog(dog);
    setFormData({
      name: dog.name,
      breed: dog.breed,
      age: dog.age.toString(),
      photo_url: dog.photo_url || '',
      fun_facts: dog.fun_facts || '',
      photos: dog.photos || [],
      videos: dog.videos || [],
      tiktok_url: dog.tiktok_url || '',
      instagram_url: dog.instagram_url || ''
    });
    setIsAddingDog(true);
  };

  const addPhoto = () => {
    if (newPhotoUrl && formData.photos.length < 10) {
      setFormData({ ...formData, photos: [...formData.photos, newPhotoUrl] });
      setNewPhotoUrl('');
    }
  };

  const removePhoto = (index) => {
    setFormData({ 
      ...formData, 
      photos: formData.photos.filter((_, i) => i !== index) 
    });
  };

  const addVideo = () => {
    if (newVideoUrl && formData.videos.length < 5) {
      setFormData({ ...formData, videos: [...formData.videos, newVideoUrl] });
      setNewVideoUrl('');
    }
  };

  const removeVideo = (index) => {
    setFormData({ 
      ...formData, 
      videos: formData.videos.filter((_, i) => i !== index) 
    });
  };

  const handleStartTraining = (dog) => {
    selectDog(dog);
    navigate('/owner/series');
  };

  const dogAvatars = [
    'https://images.unsplash.com/photo-1552053831-71594a27632d?w=200',
    'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=200',
    'https://images.unsplash.com/photo-1544456948-c7ba22fe7111?w=200',
    'https://images.unsplash.com/photo-1477884213360-7e9d7dcc1e48?w=200',
  ];

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-bounce-subtle">
            <span className="text-5xl">🐕</span>
          </div>
        </div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-gray-800 mb-2">
            My Dogs
          </h1>
          <p className="text-gray-500 font-body">
            Manage your dogs. Click a dog to start.
          </p>
        </div>

        <Dialog open={isAddingDog} onOpenChange={(open) => {
          setIsAddingDog(open);
          if (!open) {
            setEditingDog(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <button
              data-testid="add-dog-btn"
              className="flex items-center gap-2 font-heading font-bold py-3 px-6 rounded-full text-white
                bg-edupaws-navy shadow-[0_4px_0_#3C4753] hover:shadow-[0_2px_0_#3C4753] hover:translate-y-[2px] transition-all"
            >
              <Plus className="w-5 h-5" />
              Add Dog
            </button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">
                {editingDog ? 'Edit Dog Profile' : 'Add New Dog'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="mt-4">
              <Tabs defaultValue="basic" className="w-full">
                <TabsList className="grid w-full grid-cols-4 mb-4">
                  <TabsTrigger value="basic" className="font-body">Basic Info</TabsTrigger>
                  <TabsTrigger value="media" className="font-body">Photos & Videos</TabsTrigger>
                  <TabsTrigger value="social" className="font-body">Social</TabsTrigger>
                  <TabsTrigger value="fun" className="font-body">Fun Facts</TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="dog-name" className="font-body font-medium">
                      Dog's Name *
                    </Label>
                    <Input
                      id="dog-name"
                      data-testid="dog-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Buddy"
                      required
                      className="h-12 rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dog-breed" className="font-body font-medium">
                      Breed *
                    </Label>
                    <Input
                      id="dog-breed"
                      data-testid="dog-breed-input"
                      value={formData.breed}
                      onChange={(e) => setFormData({ ...formData, breed: e.target.value })}
                      placeholder="Golden Retriever"
                      required
                      className="h-12 rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dog-age" className="font-body font-medium">
                      Age (years) *
                    </Label>
                    <Input
                      id="dog-age"
                      data-testid="dog-age-input"
                      type="number"
                      min="0"
                      max="25"
                      value={formData.age}
                      onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                      placeholder="2"
                      required
                      className="h-12 rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dog-photo" className="font-body font-medium">
                      Profile Photo
                    </Label>
                    <div className="space-y-2">
                      {formData.photo_url && (
                        <div className="relative w-24 h-24 rounded-xl overflow-hidden">
                          <img src={formData.photo_url} alt="Preview" className="w-full h-full object-cover" />
                          <button type="button" onClick={() => setFormData({...formData, photo_url: ''})}
                            className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      <label className={`flex items-center justify-center gap-2 cursor-pointer h-12 rounded-xl border-2 border-dashed transition-colors bg-gray-50/50
                        ${uploading ? 'border-edupaws-sage bg-edupaws-sage/5' : 'border-gray-200 hover:border-edupaws-sage'}`}>
                        <Camera className={`w-5 h-5 ${uploading ? 'text-edupaws-sage animate-pulse' : 'text-gray-400'}`} />
                        <span className="text-sm text-gray-500 font-body">
                          {uploading ? 'Uploading...' : formData.photo_url ? 'Change photo' : 'Upload photo'}
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          data-testid="dog-photo-upload"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            if (file.size > 10 * 1024 * 1024) {
                              toast.error('File too large (max 10MB)');
                              return;
                            }
                            setUploading(true);
                            const fd = new FormData();
                            fd.append('file', file);
                            try {
                              const res = await axios.post(`${API}/upload`, fd);
                              setFormData(prev => ({...prev, photo_url: res.data.url}));
                              toast.success('Photo uploaded!');
                            } catch (err) {
                              toast.error('Upload failed');
                            } finally {
                              setUploading(false);
                            }
                          }}
                        />
                      </label>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="media" className="space-y-6">
                  {/* Photos Section */}
                  <div className="space-y-3">
                    <Label className="font-body font-medium flex items-center gap-2">
                      <Camera className="w-4 h-4 text-edupaws-sage" />
                      Photo Gallery ({formData.photos.length}/10)
                    </Label>
                    
                    {formData.photos.length > 0 && (
                      <div className="grid grid-cols-3 gap-2">
                        {formData.photos.map((photo, idx) => (
                          <div key={idx} className="relative group">
                            <img 
                              src={photo} 
                              alt={`Photo ${idx + 1}`}
                              className="w-full h-20 object-cover rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={() => removePhoto(idx)}
                              className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {formData.photos.length < 10 && (
                      <div className="flex gap-2">
                        <Input
                          value={newPhotoUrl}
                          onChange={(e) => setNewPhotoUrl(e.target.value)}
                          placeholder="Paste photo URL..."
                          data-testid="new-photo-url-input"
                          className="h-10 rounded-lg"
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPhoto())}
                        />
                        <button
                          type="button"
                          onClick={addPhoto}
                          data-testid="add-photo-btn"
                          className="px-4 h-10 bg-edupaws-sage/20 hover:bg-edupaws-sage/30 text-edupaws-navy rounded-lg transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Videos Section */}
                  <div className="space-y-3">
                    <Label className="font-body font-medium flex items-center gap-2">
                      <Video className="w-4 h-4 text-orange-500" />
                      Video Links ({formData.videos.length}/5)
                    </Label>
                    
                    {formData.videos.length > 0 && (
                      <div className="space-y-2">
                        {formData.videos.map((video, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-orange-50 px-3 py-2 rounded-lg">
                            <Play className="w-4 h-4 text-orange-500 flex-shrink-0" />
                            <span className="flex-1 text-sm text-gray-600 font-body truncate">{video}</span>
                            <button
                              type="button"
                              onClick={() => removeVideo(idx)}
                              className="p-1 hover:bg-red-100 rounded text-red-500"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {formData.videos.length < 5 && (
                      <div className="flex gap-2">
                        <Input
                          value={newVideoUrl}
                          onChange={(e) => setNewVideoUrl(e.target.value)}
                          placeholder="Paste video URL (YouTube, Vimeo, etc.)..."
                          data-testid="new-video-url-input"
                          className="h-10 rounded-lg"
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addVideo())}
                        />
                        <button
                          type="button"
                          onClick={addVideo}
                          data-testid="add-video-btn"
                          className="px-4 h-10 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-lg transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="social" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="instagram-url" className="font-body font-medium flex items-center gap-2">
                      <Instagram className="w-4 h-4 text-pink-500" />
                      Instagram URL
                    </Label>
                    <Input
                      id="instagram-url"
                      data-testid="instagram-url-input"
                      value={formData.instagram_url}
                      onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
                      placeholder="https://instagram.com/your_dog"
                      className="h-12 rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tiktok-url" className="font-body font-medium flex items-center gap-2">
                      <ExternalLink className="w-4 h-4 text-gray-700" />
                      TikTok URL
                    </Label>
                    <Input
                      id="tiktok-url"
                      data-testid="tiktok-url-input"
                      value={formData.tiktok_url}
                      onChange={(e) => setFormData({ ...formData, tiktok_url: e.target.value })}
                      placeholder="https://tiktok.com/@your_dog"
                      className="h-12 rounded-xl"
                    />
                  </div>
                  <p className="text-xs text-gray-400 font-body">
                    Add your dog's social media accounts to share with the community.
                  </p>
                </TabsContent>

                <TabsContent value="fun" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="fun-facts" className="font-body font-medium flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-yellow-500" />
                      Fun Facts About Your Dog
                    </Label>
                    <Textarea
                      id="fun-facts"
                      data-testid="fun-facts-input"
                      value={formData.fun_facts}
                      onChange={(e) => setFormData({ ...formData, fun_facts: e.target.value })}
                      placeholder="Tell us what makes your dog special! Favorite toys, tricks they know, funny habits..."
                      className="min-h-[150px] rounded-xl resize-none"
                    />
                    <p className="text-xs text-gray-400 font-body">
                      Share personality quirks, favorite activities, or memorable moments!
                    </p>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="flex gap-3 pt-6 border-t mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddingDog(false);
                    setEditingDog(null);
                    resetForm();
                  }}
                  className="flex-1 font-heading font-bold py-3 rounded-full border-2 border-gray-200 text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="save-dog-btn"
                  disabled={submitting || uploading}
                  className="flex-1 font-heading font-bold py-3 rounded-full text-white
                    bg-edupaws-navy shadow-[0_4px_0_#3C4753] hover:shadow-[0_2px_0_#3C4753] hover:translate-y-[2px] transition-all
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:translate-y-0"
                >
                  {submitting ? 'Saving...' : uploading ? 'Wait for upload...' : editingDog ? 'Update' : 'Add Dog'}
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Dogs Grid */}
      {dogs.length === 0 ? (
        <div className="text-center py-16 glass-card rounded-3xl border-2 border-dashed border-edupaws-sage/30">
          <div className="w-24 h-24 bg-edupaws-sage/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <PawPrint className="w-12 h-12 text-edupaws-sage" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-gray-800 mb-4">
            No Dogs Yet
          </h2>
          <p className="text-gray-500 font-body mb-6 max-w-md mx-auto">
            Add your first dog to start tracking their training progress through our curriculum!
          </p>
          <button
            onClick={() => setIsAddingDog(true)}
            className="inline-flex items-center gap-2 font-heading font-bold py-3 px-8 rounded-full text-white
              bg-edupaws-navy shadow-[0_4px_0_#3C4753] hover:shadow-[0_2px_0_#3C4753] hover:translate-y-[2px] transition-all"
          >
            <Plus className="w-5 h-5" />
            Add Your First Dog
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {dogs.map((dog, idx) => (
            <div
              key={dog.id}
              data-testid={`dog-card-${dog.id}`}
              className={`glass-card rounded-3xl overflow-hidden border-2 transition-all duration-300
                ${activeDog?.id === dog.id 
                  ? 'border-edupaws-navy shadow-lg' 
                  : 'border-white/40 hover:border-edupaws-sage/40 hover:shadow-md'
                }`}
            >
              {/* Dog Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={dog.photo_url || dogAvatars[idx % dogAvatars.length]}
                  alt={dog.name}
                  className="w-full h-full object-cover"
                />
                {/* Active Badge */}
                {activeDog?.id === dog.id && (
                  <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-edupaws-navy text-white text-xs font-body font-medium px-3 py-1.5 rounded-full">
                    <Check className="w-3 h-3" />
                    Currently Training
                  </div>
                )}
                
                {/* Media indicators */}
                <div className="absolute bottom-3 right-3 flex gap-2">
                  {dog.photos && dog.photos.length > 0 && (
                    <span className="bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-body px-2 py-1 rounded-full flex items-center gap-1">
                      <Image className="w-3 h-3" /> {dog.photos.length}
                    </span>
                  )}
                  {dog.videos && dog.videos.length > 0 && (
                    <span className="bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-body px-2 py-1 rounded-full flex items-center gap-1">
                      <Video className="w-3 h-3" /> {dog.videos.length}
                    </span>
                  )}
                </div>
              </div>

              {/* Dog Info */}
              <div className="p-5">
                <h3 className="font-heading text-2xl font-bold text-gray-800 mb-1">
                  {dog.name}
                </h3>
                <p className="text-gray-500 font-body mb-3">
                  {dog.breed} • {dog.age} year{dog.age !== 1 ? 's' : ''} old
                </p>

                {/* Fun Facts Preview */}
                {dog.fun_facts && (
                  <div className="bg-yellow-50 rounded-xl p-3 mb-4">
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-gray-600 font-body line-clamp-2">
                        {dog.fun_facts}
                      </p>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="space-y-2">
                  {/* Start Training Button - Primary CTA */}
                  <button
                    onClick={() => handleStartTraining(dog)}
                    data-testid={`train-dog-${dog.id}`}
                    className="w-full flex items-center justify-center gap-2 font-heading font-bold py-3 rounded-full text-white
                      bg-edupaws-navy shadow-[0_3px_0_#3C4753] hover:shadow-[0_1px_0_#3C4753] hover:translate-y-[2px] transition-all"
                  >
                    <BookOpen className="w-5 h-5" />
                    {activeDog?.id === dog.id ? 'Continue Training' : 'Start Training'}
                    <ChevronRight className="w-4 h-4" />
                  </button>

                  {/* Secondary Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setViewingDog(dog)}
                      data-testid={`view-dog-${dog.id}`}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 font-body font-medium text-sm transition-colors"
                    >
                      View Profile
                    </button>
                    <button
                      onClick={() => openEditDialog(dog)}
                      data-testid={`edit-dog-${dog.id}`}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(dog)}
                      data-testid={`delete-dog-${dog.id}`}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-red-50 hover:bg-red-100 text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View Dog Profile Dialog */}
      <Dialog open={!!viewingDog} onOpenChange={(open) => !open && setViewingDog(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          {viewingDog && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl flex items-center gap-3">
                  <PawPrint className="w-6 h-6 text-edupaws-sage" />
                  {viewingDog.name}'s Profile
                </DialogTitle>
              </DialogHeader>

              <div className="mt-4 space-y-6">
                {/* Main Photo */}
                <div className="relative rounded-2xl overflow-hidden">
                  <img
                    src={viewingDog.photo_url || dogAvatars[0]}
                    alt={viewingDog.name}
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute bottom-4 left-4">
                    <h3 className="font-heading text-2xl font-bold text-white drop-shadow-lg">
                      {viewingDog.name}
                    </h3>
                    <p className="text-white/90 font-body drop-shadow-lg">
                      {viewingDog.breed} • {viewingDog.age} year{viewingDog.age !== 1 ? 's' : ''} old
                    </p>
                  </div>
                </div>

                {/* Fun Facts */}
                {viewingDog.fun_facts && (
                  <div className="bg-yellow-50 rounded-2xl p-5">
                    <h4 className="font-heading font-bold text-gray-800 mb-2 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-yellow-500" />
                      Fun Facts
                    </h4>
                    <p className="text-gray-600 font-body">{viewingDog.fun_facts}</p>
                  </div>
                )}

                {/* Photo Gallery */}
                {viewingDog.photos && viewingDog.photos.length > 0 && (
                  <div>
                    <h4 className="font-heading font-bold text-gray-800 mb-3 flex items-center gap-2">
                      <Camera className="w-5 h-5 text-edupaws-sage" />
                      Photo Gallery
                    </h4>
                    <div className="grid grid-cols-3 gap-2">
                      {viewingDog.photos.map((photo, idx) => (
                        <img
                          key={idx}
                          src={photo}
                          alt={`${viewingDog.name} photo ${idx + 1}`}
                          className="w-full h-24 object-cover rounded-xl hover:opacity-90 cursor-pointer transition-opacity"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Videos */}
                {viewingDog.videos && viewingDog.videos.length > 0 && (
                  <div>
                    <h4 className="font-heading font-bold text-gray-800 mb-3 flex items-center gap-2">
                      <Video className="w-5 h-5 text-orange-500" />
                      Videos
                    </h4>
                    <div className="space-y-2">
                      {viewingDog.videos.map((video, idx) => (
                        <a
                          key={idx}
                          href={video}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 bg-orange-50 hover:bg-orange-100 px-4 py-3 rounded-xl transition-colors"
                        >
                          <Play className="w-5 h-5 text-orange-500" />
                          <span className="flex-1 text-sm text-gray-600 font-body truncate">{video}</span>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Social Media Links */}
                {(viewingDog.tiktok_url || viewingDog.instagram_url) && (
                  <div>
                    <h4 className="font-heading font-bold text-gray-800 mb-3 flex items-center gap-2">
                      <ExternalLink className="w-5 h-5 text-edupaws-sage" />
                      Social Media
                    </h4>
                    <div className="flex gap-3">
                      {viewingDog.instagram_url && (
                        <a href={viewingDog.instagram_url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-2 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 text-white px-4 py-2 rounded-xl hover:opacity-90 transition-opacity">
                          <Instagram className="w-4 h-4" />
                          <span className="text-sm font-body">Instagram</span>
                        </a>
                      )}
                      {viewingDog.tiktok_url && (
                        <a href={viewingDog.tiktok_url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-xl hover:bg-gray-800 transition-colors">
                          <ExternalLink className="w-4 h-4" />
                          <span className="text-sm font-body">TikTok</span>
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Start Training CTA */}
                <button
                  onClick={() => {
                    setViewingDog(null);
                    handleStartTraining(viewingDog);
                  }}
                  className="w-full flex items-center justify-center gap-2 font-heading font-bold py-4 rounded-full text-white
                    bg-edupaws-navy shadow-[0_4px_0_#3C4753] hover:shadow-[0_2px_0_#3C4753] hover:translate-y-[2px] transition-all"
                >
                  <BookOpen className="w-5 h-5" />
                  Start Training with {viewingDog.name}
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </OwnerLayout>
  );
}
